/**
 * anon-key.ts — anonymous public-API key provisioning (PRI-496 slice 1).
 *
 * The public API's account-free path (dogfooding — the SPA uses the exact
 * flow a customer's agent uses):
 *
 *   POST /v1/sandbox        → pv_test_ sandbox key (no auth, IP-capped)
 *   POST /v1/keys/upgrade   → pv_live_ key + 6,000s free grant
 *                             (authed with the pv_test_ key)
 *
 * This module owns provisioning, localStorage persistence, and expiry so the
 * anon web flows (PRI-500/503 consumption work) share one abstraction.
 *
 * Deliberate properties:
 *  * NEVER auto-provisions on import/page load — sandbox + upgrade grants are
 *    IP-capped server-side; a key is only minted when a flow explicitly calls
 *    provisionAnonKey().
 *  * Idempotent per browser: an unexpired stored key short-circuits.
 *  * Single-flight: concurrent calls share one in-flight provisioning.
 *  * Failures never leave partial state (test keys are not persisted).
 *
 * NOTE (auth planes): these pv_ keys authenticate PUBLIC endpoints only. The
 * signed-in web app keeps using Clerk session JWTs via the token registry
 * (token.ts); this module is for signed-OUT flows.
 */
import { API_BASE_URL } from './base'

export const ANON_KEY_STORAGE_KEY = 'primate:anon-live-key'

/**
 * Window event dispatched whenever the stored anon key changes (provisioned
 * or cleared) so UI state (quota bar enablement, trial badges) can react
 * without polling localStorage. PRI-503: anon free-trial flow.
 */
export const ANON_KEY_CHANGED_EVENT = 'primate:anon-key-changed'

function notifyChanged(): void {
  try { window.dispatchEvent(new Event(ANON_KEY_CHANGED_EVENT)) } catch { /* SSR/tests */ }
}

export interface AnonKey {
  /** The pv_live_ free-grant key. */
  apiKey: string
  /** ISO expiry (30-day idle-key TTL server-side). */
  expiresAt: string
  /** Free grant size at mint time (informational; server meters usage). */
  grantSeconds: number | null
}

interface ApiErrorShape {
  code?: string
  status?: number
}

async function postJson<T>(path: string, opts?: { bearer?: string }): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Primate-Client': 'web',
      ...(opts?.bearer ? { Authorization: `Bearer ${opts.bearer}` } : {}),
    },
    body: JSON.stringify({}),
  })
  const body = await res.json().catch(() => ({} as Record<string, unknown>))
  if (!res.ok) {
    const err = (body as { error?: { code?: string; message?: string } }).error
    throw Object.assign(
      new Error(err?.message ?? `API error: ${res.status}`),
      { code: err?.code, status: res.status } satisfies ApiErrorShape,
    )
  }
  return body as T
}

function readStored(): AnonKey | null {
  try {
    const raw = localStorage.getItem(ANON_KEY_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as AnonKey
    if (!parsed.apiKey || !parsed.expiresAt) return null
    return parsed
  } catch {
    return null
  }
}

/**
 * Return the stored anon live key if present and unexpired; clears storage
 * (and returns null) when the key has expired.
 */
export function getStoredAnonKey(): AnonKey | null {
  const stored = readStored()
  if (!stored) return null
  if (new Date(stored.expiresAt).getTime() <= Date.now()) {
    clearAnonKey()
    return null
  }
  return stored
}

/** Drop the stored key (call on 401 invalid_api_key from the server). */
export function clearAnonKey(): void {
  try { localStorage.removeItem(ANON_KEY_STORAGE_KEY) } catch { /* private mode */ }
  notifyChanged()
}

let inFlight: Promise<AnonKey> | null = null

/**
 * Get-or-provision an anonymous pv_live_ free-grant key.
 * Short-circuits to the stored key when one is unexpired; otherwise runs
 * sandbox → upgrade and persists the result. Errors carry the server error
 * `code` (e.g. sandbox_limit_exceeded, upgrade_limit_exceeded).
 */
export function provisionAnonKey(): Promise<AnonKey> {
  const existing = getStoredAnonKey()
  if (existing) return Promise.resolve(existing)
  if (inFlight) return inFlight

  inFlight = (async () => {
    // 1. Sandbox: pv_test_ key, no auth.
    const sandbox = await postJson<{ api_key: string }>('/v1/sandbox')
    // 2. Upgrade: pv_live_ + free grant, authed with the test key.
    const upgraded = await postJson<{
      api_key: string
      expires_at: string
      grant_seconds?: number
    }>('/v1/keys/upgrade', { bearer: sandbox.api_key })

    const key: AnonKey = {
      apiKey: upgraded.api_key,
      expiresAt: upgraded.expires_at,
      grantSeconds: upgraded.grant_seconds ?? null,
    }
    try { localStorage.setItem(ANON_KEY_STORAGE_KEY, JSON.stringify(key)) } catch { /* private mode */ }
    notifyChanged()
    return key
  })().finally(() => { inFlight = null })

  return inFlight
}
